import { AnimationScenario, scenarioFromPane } from './AnimationScenario';
import { AnimationStyle } from './AnimationStyle';
import { PaneChannelTiming, PaneChannelTimings } from './AnimationTimingTypes';
import { DataStorePaneState } from './DataStorePaneState';
import { DataStorePaths } from './DataStorePaths';
import { IDataStore } from './IDataStore';
import { MarkAllAnimationParams } from './MarkAllAnimationParams';
import { MarkAllAnimationParamsTimingResolver } from './MarkAllAnimationParamsTimingResolver';
import { classifyPaneMarks } from './PaneAnimationInfoClassification';
import { computePaneAnimationSamples } from './PaneAnimationInfoSampling';
import {
  createPaneAnimationTimingState,
  createPaneWorkingParams,
  finalizePaneAnimationTimingState,
  PaneAnimationTimingState,
} from './PaneAnimationInfoTiming';
import { PaneStateAttribute } from './PaneStateAttribute';
import { WarpingFunction } from './WarpingFunctionMapUtil';

/**
 * Per-pane animation info. Classifies marks as enter/move/exit.
 * C++ Reference: PaneAnimationInfo.cpp
 *
 * Work items:
 * - W-20396683: Mark ID classification (current)
 * - W-20396690: state0, state1, interpolation
 * - W-20396697: animation params, timing
 */
export class PaneAnimationInfo {
  private paneDescriptorId: string;
  private enterMarkIds: number[] = [];
  private moveMarkIds: number[] = [];
  private moveLabelMarkIds: number[] = [];
  private allExitMarkIds: number[] = [];
  private exitMarkIds: number[] = [];
  private movingPaneSampleMarkIds: number[] = [];
  private enteringPaneSampleMarkIds: number[] = [];
  private movingUserPaneSampleMarkIds: number[] = [];
  private enteringUserPaneSampleMarkIds: number[] = [];
  private primType = 0;

  // Animation style for this pane (Synced vs Phased)
  private animationStyle: AnimationStyle = AnimationStyle.Phased;

  // Resolved timings consumed by non-marks and mark interpolation
  private timingState: PaneAnimationTimingState = createPaneAnimationTimingState(AnimationScenario.Other);

  constructor(paneDescriptorId: string) {
    this.paneDescriptorId = paneDescriptorId;
  }

  private computeSampleMarkIds(startState: DataStorePaneState | null, endState: DataStorePaneState): void {
    const samples = computePaneAnimationSamples(
      this.paneDescriptorId,
      startState,
      endState,
      this.moveMarkIds,
      this.enterMarkIds,
      this.allExitMarkIds
    );
    this.movingPaneSampleMarkIds = samples.movingPaneSampleMarkIds;
    this.enteringPaneSampleMarkIds = samples.enteringPaneSampleMarkIds;
    this.movingUserPaneSampleMarkIds = samples.movingUserPaneSampleMarkIds;
    this.enteringUserPaneSampleMarkIds = samples.enteringUserPaneSampleMarkIds;
    this.exitMarkIds = samples.exitMarkIds;
  }

  private applyMarkClassification(fromDs: IDataStore | null, toDs: IDataStore): void {
    const classification = classifyPaneMarks(fromDs, toDs, this.paneDescriptorId);
    this.enterMarkIds = classification.enterMarkIds;
    this.moveMarkIds = classification.moveMarkIds;
    this.moveLabelMarkIds = classification.moveLabelMarkIds;
    this.allExitMarkIds = classification.allExitMarkIds;
    this.exitMarkIds = classification.exitMarkIds;
  }

  private resetResolvedTimingState(toDs: IDataStore): void {
    this.timingState = createPaneAnimationTimingState(scenarioFromPane(toDs, this.paneDescriptorId));
  }

  private createWorkingParams(): MarkAllAnimationParams {
    return createPaneWorkingParams(this.animationStyle, this.timingState.shadowScenario);
  }

  private finalizeResolvedParams(resolvedParams: MarkAllAnimationParams, totalDuration: number): void {
    this.timingState = finalizePaneAnimationTimingState(this.timingState, resolvedParams, totalDuration);
  }

  // Build all per-pane animation data for this transition.
  // - Figure out which marks enter, move, exit, and which labels move.
  // - Pick representative sample marks for pane/user-pane movement, and narrow exitMarkIds
  //   to exits that belong to panes/user-panes participating in the transition.
  // - Start from style/scenario default timings, remove channels with no real work,
  //   compress empty time gaps, compute the raw total duration, then normalize to 0..1.
  // - Store the resolved timings and easing functions in shadow* fields for later use by
  //   non-mark animation and mark interpolation.
  build(fromDs: IDataStore | null, toDs: IDataStore, animationStyle: AnimationStyle): void {
    this.animationStyle = animationStyle;
    this.primType = DataStorePaths.primitiveType(toDs, this.paneDescriptorId);
    if (fromDs === null) {
      this.applyMarkClassification(fromDs, toDs);
    } else {
      this.gatherMarkIdVectors(fromDs, toDs);
    }
    this.resetResolvedTimingState(toDs);
    let workingParams = this.createWorkingParams();

    const startState = fromDs !== null ? new DataStorePaneState(fromDs) : null;
    const endState = new DataStorePaneState(toDs);
    this.computeSampleMarkIds(startState, endState);

    // Step 1: cull channels with no marks
    workingParams = MarkAllAnimationParamsTimingResolver.cullAnimationsWithNoMarks(
      workingParams,
      this.enterMarkIds,
      this.moveMarkIds.length > 0 ? this.moveMarkIds : this.moveLabelMarkIds,
      this.exitMarkIds
    );
    workingParams = MarkAllAnimationParamsTimingResolver.cullNoopAnimations(
      workingParams,
      this.paneDescriptorId,
      startState,
      endState,
      this.enterMarkIds,
      this.moveMarkIds,
      this.moveLabelMarkIds,
      this.exitMarkIds,
      this.movingUserPaneSampleMarkIds
    );

    this.timingState.shadowWorkingParams = workingParams;
  }

  // C++ GatherMarkIdVectors() - set operations: enter=TO-FROM, move=TO∩FROM, exit=FROM-TO
  private gatherMarkIdVectors(fromDs: IDataStore, toDs: IDataStore): void {
    this.applyMarkClassification(fromDs, toDs);
  }

  getPaneDescriptorId(): string {
    return this.paneDescriptorId;
  }

  getPrimType(): number {
    return this.primType;
  }

  getEnterMarkIds(): number[] {
    return this.enterMarkIds;
  }

  getMoveMarkIds(): number[] {
    return this.moveMarkIds;
  }

  getMoveLabelMarkIds(): number[] {
    return this.moveLabelMarkIds;
  }

  getMovingPaneSampleMarkIds(): number[] {
    return this.movingPaneSampleMarkIds;
  }

  getEnteringPaneSampleMarkIds(): number[] {
    return this.enteringPaneSampleMarkIds;
  }

  getMovingUserPaneSampleMarkIds(): number[] {
    return this.movingUserPaneSampleMarkIds;
  }

  getEnteringUserPaneSampleMarkIds(): number[] {
    return this.enteringUserPaneSampleMarkIds;
  }

  getExitMarkIds(): number[] {
    return this.exitMarkIds;
  }

  hasMarksToAnimate(): boolean {
    return this.enterMarkIds.length > 0 || this.moveMarkIds.length > 0 || this.moveLabelMarkIds.length > 0 || this.exitMarkIds.length > 0;
  }

  getSummary(): string {
    const total = this.enterMarkIds.length + this.moveMarkIds.length + this.exitMarkIds.length;
    return `Found ${total} marks: ${this.moveMarkIds.length} MOVE, ${this.enterMarkIds.length} ENTER, ${this.exitMarkIds.length} EXIT`;
  }

  static getPaneDescriptorsSet(ds: IDataStore | null): Set<string> {
    return DataStorePaths.getPaneDescriptorsSet(ds);
  }

  getAnimationStyle(): AnimationStyle {
    return this.animationStyle;
  }

  /** Integer value of AnimationStyle for telemetry. */
  getAnimationStyleInt(): number {
    return this.animationStyle as number;
  }

  /** Integer value of AnimationScenario for telemetry. */
  getShadowScenarioInt(): number {
    return this.timingState.shadowScenario as unknown as number;
  }

  /** Raw (pre-normalization) total duration in slot units. */
  getShadowTotalDuration(): number {
    return this.timingState.shadowTotalDuration;
  }

  getShadowNonMarksTiming(): PaneChannelTiming | null {
    return this.timingState.shadowNonMarksTiming;
  }

  getShadowNonMarkAttributeTimingOverrides(): Map<PaneStateAttribute, PaneChannelTiming> {
    return new Map(this.timingState.shadowNonMarkAttributeTimingOverrides);
  }

  getShadowOverallWarpingFunction(): WarpingFunction | null {
    return this.timingState.shadowOverallWarpingFunction;
  }

  /**
   * Build per-channel timing info for mark interpolation.
   * Returns null if shadow timings have not been computed yet.
   * C++ Reference: PaneAnimationInfo::GetAnimationParams() per AnimationType.
   */
  buildPaneChannelTimings(): PaneChannelTimings | null {
    if (!this.hasMarksToAnimate()) {
      return null;
    }
    if (!this.timingState.shadowEnterTiming || !this.timingState.shadowMoveTiming || !this.timingState.shadowExitTiming) {
      return null;
    }
    return {
      enterTiming: this.timingState.shadowEnterTiming,
      moveTiming: this.timingState.shadowMoveTiming,
      exitTiming: this.timingState.shadowExitTiming,
      enterAttributeTimingOverrides: new Map(this.timingState.shadowEnterAttributeTimingOverrides),
      moveAttributeTimingOverrides: new Map(this.timingState.shadowMoveAttributeTimingOverrides),
      exitAttributeTimingOverrides: new Map(this.timingState.shadowExitAttributeTimingOverrides),
      enterMarkIdSet: new Set(this.enterMarkIds),
      moveMarkIdSet: new Set(this.moveMarkIds),
      moveLabelMarkIdSet: new Set(this.moveLabelMarkIds),
      exitMarkIdSet: new Set(this.exitMarkIds),
    };
  }

  // C++ MarkAnimationController::BuildPaneAnimationInfoMap()
  static buildForAllPanes(fromDs: IDataStore | null, toDs: IDataStore, animationStyle: AnimationStyle): Map<string, PaneAnimationInfo> {
    const result = new Map<string, PaneAnimationInfo>();
    const panesToAnimate = DataStorePaths.getPanesToAnimate(fromDs, toDs);
    const fromPanes = DataStorePaths.getPaneDescriptorsSet(fromDs);

    for (const paneId of panesToAnimate) {
      const paneInfo = new PaneAnimationInfo(paneId);
      const isEnteringPane = !fromPanes.has(paneId);
      paneInfo.build(isEnteringPane ? null : fromDs, toDs, animationStyle);
      result.set(paneId, paneInfo);
    }

    const paneInfos = Array.from(result.values());
    const unresolvedParams: MarkAllAnimationParams[] = paneInfos.map((paneInfo) => {
      const params = paneInfo.timingState.shadowWorkingParams?.clone();
      if (!params) {
        throw new Error(`Missing unresolved animation params for pane ${paneInfo.getPaneDescriptorId()}`);
      }
      return params;
    });
    const gaplessParams = MarkAllAnimationParamsTimingResolver.removeEmptyGapsForAll(unresolvedParams);
    paneInfos.forEach((paneInfo, index) => {
      const params = gaplessParams[index];
      if (!params) {
        return;
      }
      const totalDuration = MarkAllAnimationParamsTimingResolver.computeEndTime(params);
      const normalizedParams = MarkAllAnimationParamsTimingResolver.normalizeTimings(params, totalDuration);
      paneInfo.finalizeResolvedParams(normalizedParams, totalDuration);
    });
    return result;
  }
}
